import { useState } from 'react';
import { Link } from 'react-router-dom';
import { COMPANY_INFO, REVIEWS } from '../data/staticData';
import PriceCalculator from '../components/PriceCalculator';
import ContactForm from '../components/ContactForm';

export default function NewRoofPage() {
  const [openFaq, setOpenFaq] = useState([0]);
  const roofPriceTable = [
    { type: "Betontagsten", price: "ca. 900–1.400 kr.", lifespan: "30–40 år", link: "/ydelser/betontag" },
    { type: "Tegltag", price: "ca. 1.200–1.800 kr.", lifespan: "50+ år", link: "/ydelser/tegltag" },
    { type: "Tagpap", price: "ca. 800–1.400 kr.", lifespan: "30–50 år", link: "/ydelser/tagpap" },
    { type: "Ståltag", price: "ca. 700–1.200 kr.", lifespan: "30–40 år", link: "/ydelser/staaltag" },
    { type: "Skifer", price: "ca. 1.600–2.500 kr.", lifespan: "60+ år", link: "/ydelser/skifertag" }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 lg:pt-24 pb-16 space-y-16 sm:space-y-24">
      
      {/* 1. Header */}
      <div className="text-left max-w-4xl space-y-6">
        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-stone-900 tracking-tight uppercase">
          Nyt tag – pris, tagtyper og professionel tagudskiftning i hele Danmark
        </h1>
        <h2 className="text-xl sm:text-2xl font-bold text-stone-800 uppercase">
          Professionel tagudskiftning eller tagrenovering
        </h2>
        <p className="text-stone-700 text-base sm:text-lg leading-relaxed">
          Få professionel udskiftning af dit tag med{' '}
          <Link to="/kontakt" className="text-[#BE0F12] font-semibold underline hover:text-black">
            Dansk Tagbyg
          </Link>{' '}
          – eksperter i tagløsninger til hus, tilbygning, sommerhus og carport siden 2016. Vi guider dig gennem hele processen og hjælper dig med at vælge den rigtige tagtype, det rette materiale og det udtryk, der passer til din bolig.
        </p>
        <p className="text-stone-700 text-base leading-relaxed">
          Vi tager os af både store og små tagprojekter og sørger for at overholde de aftalte tidsrammer. Vi kører i hele Danmark og tilbyder <strong>konkurrencedygtige priser</strong>, hvor kvalitet, kommunikation og godt håndværk er i fokus.
        </p>
        <p className="text-stone-700 text-base leading-relaxed">
          Vi er bedømt{' '}
          <a
            href="https://www.anmeld-haandvaerker.dk/tagdaekker/dansk-tagbyg-aps"
            target="_blank"
            rel="noreferrer"
            className="text-[#BE0F12] font-bold underline hover:text-black"
          >
            4,8/5 stjerner
          </a>{' '}
          af vores kunder, og vi lever af tilfredse boligejere, der trygt kan anbefale os videre.
        </p>
      </div>

      {/* Stor erfaring og dokumenteret kvalitet */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 space-y-4 text-stone-700 leading-relaxed text-sm sm:text-base">
          <h3 className="text-2xl font-bold text-stone-900 uppercase">
            Stor erfaring og dokumenteret kvalitet
          </h3>
          <p>
            Dansk Tagbyg er specialiseret i tagudskiftning og tagrenovering i hele Danmark. Virksomheden ledes af Lasse, som har solid erfaring inden for tagbranchen og står personligt inde for kvaliteten i hvert eneste projekt.
          </p>
          <p>
            Vi arbejder efter gældende branchestandarder og er tilknyttet relevante{' '}
            <Link to="/garantier" className="text-[#BE0F12] font-semibold underline hover:text-black">
              garantiordninger
            </Link>
            , så du som boligejer er ekstra sikret – både under og efter arbejdet.
          </p>
          <p>
            Hos os møder du ikke en anonym salgsafdeling. Du taler direkte med fagfolk, der har praktisk erfaring med tagkonstruktioner, materialer og de udfordringer, der kan opstå undervejs.
          </p>
          <p>
            Vil du vide mere om os og vores tilgang, kan du{' '}
            <Link to="/profil" className="text-[#BE0F12] font-semibold underline hover:text-black">
              læse mere om Dansk Tagbyg her
            </Link>
            .
          </p>
          <div className="pt-2">
            <a
              href="#prisberegner-sektion"
              className="inline-flex items-center gap-2 px-6 py-3 bg-stone-900 hover:bg-black text-white font-bold text-sm uppercase transition-colors"
            >
              <span>Beregn tagpris online</span>
              <i className="fa-solid fa-arrow-down text-xs"></i>
            </a>
          </div>
        </div>
        <div className="lg:col-span-5">
          <img
            src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/2022-03-31-175151_75ccc626.jpg"
            alt="Nyt tag på hus"
            className="w-full h-80 object-cover border border-stone-200 shadow-sm"
          />
        </div>
      </div>

      {/* Investér i et nyt tag og optimer din bolig */}
      <div className="p-8 sm:p-12 bg-stone-50 border border-stone-200 space-y-6">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 uppercase">
          Investér i et nyt tag og optimer din bolig
        </h2>
        <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
          Et <strong>nyt tag</strong> er ikke bare en nødvendig udskiftning – det er en investering i din bolig. Et sundt og korrekt udført tag kan:
        </p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-stone-800 font-medium">
          <li className="flex items-center gap-2.5">
            <i className="fa-solid fa-check text-emerald-600"></i>
            <span>Forbedre indeklimaet</span>
          </li>
          <li className="flex items-center gap-2.5">
            <i className="fa-solid fa-check text-emerald-600"></i>
            <span>Reducere varmetab</span>
          </li>
          <li className="flex items-center gap-2.5">
            <i className="fa-solid fa-check text-emerald-600"></i>
            <span>Mindske risikoen for fugtskader</span>
          </li>
          <li className="flex items-center gap-2.5">
            <i className="fa-solid fa-check text-emerald-600"></i>
            <span>Øge boligens værdi</span>
          </li>
          <li className="flex items-center gap-2.5 sm:col-span-2">
            <i className="fa-solid fa-check text-emerald-600"></i>
            <span>Give huset et markant visuelt løft</span>
          </li>
        </ul>
        <p className="text-stone-700 text-sm sm:text-base leading-relaxed pt-2">
          Er dit tag slidt, utæt eller tæt på sin forventede levetid, kan det være en god idé at få det vurderet i tide. Vi giver dig en ærlig vurdering af tagets stand og rådgiver dig om den løsning, der bedst kan betale sig – både nu og på længere sigt.
        </p>
        <div>
          <Link
            to="/kontakt"
            className="text-stone-900 font-bold underline hover:text-[#BE0F12]"
          >
            Kontakt os i dag for en uforpligtende snak om dine muligheder →
          </Link>
        </div>
      </div>

      {/* Hvad koster et nyt tag? */}
      <div className="space-y-6">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 uppercase">
          Hvad koster et nyt tag?
        </h2>
        <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
          <strong>Prisen på et nyt tag</strong> afhænger af tagtype, størrelse, konstruktion og adgangsforhold. Når man undersøger prisen på nyt tag, er det især materialevalg og tagets kompleksitet, der spiller ind. De fleste parcelhuse ligger typisk i et samlet spænd fra ca. 120.000 kr. og op, men det kan både være lavere og højere afhængigt af forholdene.
        </p>

        {/* Tabel */}
        <div className="overflow-x-auto border border-stone-200">
          <table className="w-full text-left border-collapse text-sm">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-100 text-stone-900">
                <th className="p-4 font-bold">Tagtype</th>
                <th className="p-4 font-bold">Typisk pris pr. m²</th>
                <th className="p-4 font-bold">Forventet levetid</th>
                <th className="p-4 font-bold">Læs mere</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-200 text-stone-700">
              {roofPriceTable.map((r) => (
                <tr key={r.type} className="hover:bg-stone-50 transition-colors">
                  <td className="p-4 font-bold text-stone-900">{r.type}</td>
                  <td className="p-4">{r.price}</td>
                  <td className="p-4 font-semibold text-stone-800">{r.lifespan}</td>
                  <td className="p-4">
                    <Link to={r.link} className="text-[#BE0F12] font-semibold hover:underline">
                      Se {r.type}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-stone-500 italic">
          Priserne er vejledende og afhænger af konstruktion, undertag, isolering, stillads m.m.
        </p>

        {/* Hvad koster 100 kvm */}
        <div className="pt-4 space-y-3 text-stone-700 text-sm sm:text-base leading-relaxed">
          <h3 className="text-xl font-bold text-stone-900">
            Hvad koster 100 kvm nyt tag?
          </h3>
          <p>
            Et <strong>nyt tag</strong> på 100 m² koster typisk mellem 90.000 og 160.000 kr. afhængigt af materialevalg og kompleksitet.
          </p>
          <p>
            Mange boligejere spørger specifikt til prisen på nyt tag på parcelhus, og her afhænger det især af tagets hældning, undertag og eventuelle kviste. Den mest præcise måde at få svar på er en konkret gennemgang af din bolig. Vi kommer gerne ud og tager et grundigt kig og giver et <strong>uforpligtende tilbud</strong>, som du kan forholde dig til.
          </p>
        </div>
      </div>

      {/* 3 priseksempler fra virkelige projekter */}
      <div className="space-y-6">
        <h3 className="text-2xl font-bold text-stone-900 uppercase">
          3 priseksempler fra virkelige projekter
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-6 bg-white border border-stone-200 shadow-sm space-y-3">
            <h4 className="font-bold text-base text-stone-900">
              Parcelhus – 168 m² – Nyt betontag
            </h4>
            <p className="text-xs text-stone-600">
              Komplet tagudskiftning inkl. undertag og stillads.
            </p>
            <div className="text-xl font-extrabold text-stone-900 pt-3 border-t border-stone-100">
              Pris: ca. 185.000 kr.
            </div>
          </div>

          <div className="p-6 bg-white border border-stone-200 shadow-sm space-y-3">
            <h4 className="font-bold text-base text-stone-900">
              Villa – 192 m² – Nyt tegltag
            </h4>
            <p className="text-xs text-stone-600">
              Udskiftning af tagsten, undertag og efterisolering.
            </p>
            <div className="text-xl font-extrabold text-stone-900 pt-3 border-t border-stone-100">
              Pris: ca. 265.000 kr.
            </div>
          </div>

          <div className="p-6 bg-white border border-stone-200 shadow-sm space-y-3">
            <h4 className="font-bold text-base text-stone-900">
              Hus – 142 m² – Nyt tagpap-tag
            </h4>
            <p className="text-xs text-stone-600">
              Etablering af 2-lags tagpap på eksisterende konstruktion.
            </p>
            <div className="text-xl font-extrabold text-stone-900 pt-3 border-t border-stone-100">
              Pris: ca. 145.000 kr.
            </div>
          </div>
        </div>
        <p className="text-xs text-stone-500 italic">
          (Hver bolig er forskellig – derfor udarbejder vi altid et skræddersyet tilbud til din bolig. Når vi giver tilbud på et nyt tag, er arbejdet naturligvis dækket af gældende garantiordninger, så du ikke står alene, hvis der mod forventning skulle opstå problemer.)
        </p>
      </div>

      {/* Hvad påvirker prisen, undertag og ovenpå gammelt tag */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7 space-y-6 text-stone-700 text-sm sm:text-base leading-relaxed">
          <h3 className="text-xl sm:text-2xl font-bold text-stone-900 uppercase">
            Hvad påvirker prisen på et nyt tag?
          </h3>
          <p>Prisen på et nyt tag afhænger især af:</p>
          <ul className="list-disc pl-5 space-y-1 text-stone-800">
            <li>Tagets samlede areal</li>
            <li>Hældning og kompleksitet</li>
            <li>Kviste, ovenlysvinduer og skotrender</li>
            <li>Valg af materialer</li>
            <li>Nyt undertag eller ej</li>
            <li>Isoleringsforbedringer</li>
            <li>Nedtagning og bortskaffelse</li>
            <li>Stillads og adgangsforhold</li>
          </ul>
          <p className="font-medium text-stone-900">
            Jo enklere konstruktion – jo lavere pris pr. m².
          </p>

          <h3 className="text-xl sm:text-2xl font-bold text-stone-900 uppercase pt-4">
            Skal jeg have nyt undertag, når jeg får nyt tag?
          </h3>
          <p>
            I mange tilfælde ja. <strong>Undertaget</strong> er den del af tagkonstruktionen, der beskytter mod fugt og sne, hvis vand trænger forbi tagbelægningen. Er undertaget slidt, porøst eller fra en ældre konstruktion, anbefaler vi som regel at udskifte det samtidig med taget.
          </p>
          <p>
            Hvis undertaget derimod er i god stand og relativt nyt, kan det i nogle tilfælde bevares – men det kræver en konkret vurdering. Når vi gennemgår din bolig, vurderer vi altid undertagets tilstand og rådgiver dig om, hvad der giver bedst mening både teknisk og økonomisk.
          </p>

          <h3 className="text-xl sm:text-2xl font-bold text-stone-900 uppercase pt-4">
            Kan man lægge nyt tag ovenpå det gamle?
          </h3>
          <p>
            Det afhænger af tagtype og konstruktion. I nogle tilfælde – fx ved ståltag – kan man lægge nyt tag ovenpå det eksisterende, hvis konstruktionen er sund, og der ikke er fugtskader. Det kan reducere både arbejdstid og omkostninger.
          </p>
          <p>
            Ved andre tagtyper, eller hvis der er tegn på skader, anbefaler vi at fjerne det gamle tag først. Det giver den mest holdbare løsning og mulighed for at kontrollere undertag og isolering. Vi vurderer altid konstruktionen grundigt, før vi anbefaler den ene eller anden løsning.
          </p>
        </div>
        <div className="lg:col-span-5">
          <img
            src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/2022-10-11-105356_c1fa21a6.jpg"
            alt="Tagudskiftning under udførelse"
            className="w-full h-80 object-cover border border-stone-200 shadow-sm"
          />
        </div>
      </div>

      {/* Hvornår bør du få nyt tag? & Hvilket tag skal du vælge? */}
      <div className="p-8 bg-stone-50 border border-stone-200 space-y-6">
        <h3 className="text-2xl font-bold text-stone-900 uppercase">
          Hvornår bør du få nyt tag?
        </h3>
        <p className="text-stone-700 text-sm sm:text-base">Det kan være nødvendigt at skifte taget, hvis:</p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-sm text-stone-800">
          <li className="flex items-center gap-2">
            <i className="fa-solid fa-circle-exclamation text-amber-600"></i>
            <span>Der er utætheder eller fugtskader</span>
          </li>
          <li className="flex items-center gap-2">
            <i className="fa-solid fa-circle-exclamation text-amber-600"></i>
            <span>Tagsten revner eller smuldrer</span>
          </li>
          <li className="flex items-center gap-2">
            <i className="fa-solid fa-circle-exclamation text-amber-600"></i>
            <span>Undertaget er slidt eller porøst</span>
          </li>
          <li className="flex items-center gap-2">
            <i className="fa-solid fa-circle-exclamation text-amber-600"></i>
            <span>Taget har nået sin forventede levetid</span>
          </li>
          <li className="flex items-center gap-2 sm:col-span-2">
            <i className="fa-solid fa-circle-exclamation text-amber-600"></i>
            <span>Energiregningen er unødigt høj</span>
          </li>
        </ul>
        <p className="text-stone-700 text-sm sm:text-base">
          I nogle tilfælde kan en mindre tagrenovering være nok, men ofte vil en fuld udskiftning af taget være den mest holdbare løsning på længere sigt. Er du i tvivl, giver vi gerne en ærlig vurdering af dit tags stand.
        </p>

        {/* Mulige tagtyper */}
        <div className="space-y-6 pt-6">
          <div className="text-center max-w-3xl mx-auto space-y-2">
            <h3 className="text-2xl sm:text-4xl font-extrabold text-stone-900 uppercase">
              Mulige tagtyper
            </h3>
            <p className="text-stone-600 text-sm sm:text-base">
              Valget afhænger af husets stil, budget, lokalplan og dine ønsker til vedligeholdelse og levetid. Hos Dansk Tagbyg tilbyder vi at lægge flere forskellige slags tage, alt efter hvilke muligheder din bolig giver. Læs mere om mulighederne herunder:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 pt-4">
            {/* Betontag */}
            <div className="bg-white border border-stone-200 overflow-hidden shadow-sm flex flex-col justify-between group hover:border-[#BE0F12] transition-colors">
              <div className="h-44 overflow-hidden">
                <img
                  src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/scraped-6_d670fbbd.webp"
                  alt="Betontag"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-5 flex-grow space-y-2">
                <h4 className="text-xl font-extrabold text-stone-900 uppercase">Betontag</h4>
                <p className="text-xs font-bold text-[#BE0F12] uppercase tracking-wider">Populært og med god grund</p>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Betontag kaldes også for betontagsten eller cementtagsten og er en populær form for tagbelægning, som du ser på mange danske huse...
                </p>
              </div>
              <div className="p-5 pt-0">
                <Link
                  to="/ydelser/betontag"
                  className="block text-center py-2.5 bg-stone-900 group-hover:bg-[#BE0F12] text-white text-xs font-bold uppercase tracking-wider transition-colors"
                >
                  Læs mere
                </Link>
              </div>
            </div>

            {/* Eternittag */}
            <div className="bg-white border border-stone-200 overflow-hidden shadow-sm flex flex-col justify-between group hover:border-[#BE0F12] transition-colors">
              <div className="h-44 overflow-hidden">
                <img
                  src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/scraped-4_8223e96d.webp"
                  alt="Eternittag"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-5 flex-grow space-y-2">
                <h4 className="text-xl font-extrabold text-stone-900 uppercase">Eternittag</h4>
                <p className="text-xs font-bold text-[#BE0F12] uppercase tracking-wider">Danskernes foretrukne tagtype</p>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Trænger dit hus til et nyt tag? Eternittag er en af de mest anvendte tagløsninger i Danmark – og det er ikke uden grund. Let og robust...
                </p>
              </div>
              <div className="p-5 pt-0">
                <Link
                  to="/ydelser/eternittag"
                  className="block text-center py-2.5 bg-stone-900 group-hover:bg-[#BE0F12] text-white text-xs font-bold uppercase tracking-wider transition-colors"
                >
                  Læs mere
                </Link>
              </div>
            </div>

            {/* Nyt skifertag */}
            <div className="bg-white border border-stone-200 overflow-hidden shadow-sm flex flex-col justify-between group hover:border-[#BE0F12] transition-colors">
              <div className="h-44 overflow-hidden">
                <img
                  src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/scraped-8_5e8e1de9.webp"
                  alt="Nyt skifertag"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-5 flex-grow space-y-2">
                <h4 className="text-xl font-extrabold text-stone-900 uppercase">Nyt skifertag</h4>
                <p className="text-xs font-bold text-[#BE0F12] uppercase tracking-wider">Lang levetid og minimal vedligeholdelse</p>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Overvejer du et nyt tag, og ønsker du en løsning, der kombinerer æstetik, holdbarhed og minimal vedligeholdelse? Skifertag er et oplagt valg...
                </p>
              </div>
              <div className="p-5 pt-0">
                <Link
                  to="/ydelser/skifertag"
                  className="block text-center py-2.5 bg-stone-900 group-hover:bg-[#BE0F12] text-white text-xs font-bold uppercase tracking-wider transition-colors"
                >
                  Læs mere
                </Link>
              </div>
            </div>

            {/* Ståltag */}
            <div className="bg-white border border-stone-200 overflow-hidden shadow-sm flex flex-col justify-between group hover:border-[#BE0F12] transition-colors">
              <div className="h-44 overflow-hidden">
                <img
                  src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/scraped-6_ffb0a06d.webp"
                  alt="Ståltag"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-5 flex-grow space-y-2">
                <h4 className="text-xl font-extrabold text-stone-900 uppercase">Ståltag</h4>
                <p className="text-xs font-bold text-[#BE0F12] uppercase tracking-wider">Kvalitet, der er til at betale</p>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Ståltag er for dig, der vil have den gode kvalitet uden at skulle betale en bondegård. Modstandsdygtige og solide med 50-80 års levetid...
                </p>
              </div>
              <div className="p-5 pt-0">
                <Link
                  to="/ydelser/staaltag"
                  className="block text-center py-2.5 bg-stone-900 group-hover:bg-[#BE0F12] text-white text-xs font-bold uppercase tracking-wider transition-colors"
                >
                  Læs mere
                </Link>
              </div>
            </div>

            {/* Tegltag */}
            <div className="bg-white border border-stone-200 overflow-hidden shadow-sm flex flex-col justify-between group hover:border-[#BE0F12] transition-colors">
              <div className="h-44 overflow-hidden">
                <img
                  src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/scraped-5_4aa47bc2.webp"
                  alt="Tegltag"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-5 flex-grow space-y-2">
                <h4 className="text-xl font-extrabold text-stone-900 uppercase">Tegltag</h4>
                <p className="text-xs font-bold text-[#BE0F12] uppercase tracking-wider">Klassisk løsning med en lang levetid</p>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Tagsten i ægte tegl af hårdtbrændt ler er en klassisk og velkendt løsning med mange muligheder i forhold til farver og glasur...
                </p>
              </div>
              <div className="p-5 pt-0">
                <Link
                  to="/ydelser/tegltag"
                  className="block text-center py-2.5 bg-stone-900 group-hover:bg-[#BE0F12] text-white text-xs font-bold uppercase tracking-wider transition-colors"
                >
                  Læs mere
                </Link>
              </div>
            </div>

            {/* Tagpap */}
            <div className="bg-white border border-stone-200 overflow-hidden shadow-sm flex flex-col justify-between group hover:border-[#BE0F12] transition-colors">
              <div className="h-44 overflow-hidden">
                <img
                  src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/scraped-7_458e5882.webp"
                  alt="Tagpap"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-5 flex-grow space-y-2">
                <h4 className="text-xl font-extrabold text-stone-900 uppercase">Tagpap</h4>
                <p className="text-xs font-bold text-[#BE0F12] uppercase tracking-wider">Billigere løsning med mange muligheder</p>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Tagpap er et meget fleksibelt og stærkt tag, som egner sig til de fleste konstruktioner. 100 % tæt og arkitektonisk flot med listedækning...
                </p>
              </div>
              <div className="p-5 pt-0">
                <Link
                  to="/ydelser/tagpap"
                  className="block text-center py-2.5 bg-stone-900 group-hover:bg-[#BE0F12] text-white text-xs font-bold uppercase tracking-wider transition-colors"
                >
                  Læs mere
                </Link>
              </div>
            </div>

            {/* Decra ståltag */}
            <div className="bg-white border border-stone-200 overflow-hidden shadow-sm flex flex-col justify-between group hover:border-[#BE0F12] transition-colors">
              <div className="h-44 overflow-hidden">
                <img
                  src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/scraped-1_fa4df807.webp"
                  alt="Decra ståltag"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-5 flex-grow space-y-2">
                <h4 className="text-xl font-extrabold text-stone-900 uppercase">Decra ståltag</h4>
                <p className="text-xs font-bold text-[#BE0F12] uppercase tracking-wider">Det prisbevidste og gode valg</p>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Trænger dit tag til en renovering, eller har du brug for et helt nyt tag? Decra ståltag er en billig kvalitetsløsning med 30 års garanti...
                </p>
              </div>
              <div className="p-5 pt-0">
                <Link
                  to="/ydelser/decra-staaltag"
                  className="block text-center py-2.5 bg-stone-900 group-hover:bg-[#BE0F12] text-white text-xs font-bold uppercase tracking-wider transition-colors"
                >
                  Læs mere
                </Link>
              </div>
            </div>

            {/* Tagrender */}
            <div className="bg-white border border-stone-200 overflow-hidden shadow-sm flex flex-col justify-between group hover:border-[#BE0F12] transition-colors">
              <div className="h-44 overflow-hidden">
                <img
                  src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/scraped-1_f03d5c06.webp"
                  alt="Tagrender"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-5 flex-grow space-y-2">
                <h4 className="text-xl font-extrabold text-stone-900 uppercase">Tagrender</h4>
                <p className="text-xs font-bold text-[#BE0F12] uppercase tracking-wider">Vælg de rigtige tagrender til dit hus</p>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Når du skal vælge tagrender til dit hus, er der et hav af valgmuligheder i både aluzink, zink og kobber. Inkluderet ved nyt tag...
                </p>
              </div>
              <div className="p-5 pt-0">
                <Link
                  to="/ydelser/tagrender"
                  className="block text-center py-2.5 bg-stone-900 group-hover:bg-[#BE0F12] text-white text-xs font-bold uppercase tracking-wider transition-colors"
                >
                  Læs mere
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Regler, byggetilladelse og krav ved nyt tag */}
      <div className="space-y-6">
        <h3 className="text-2xl sm:text-3xl font-extrabold text-stone-900 uppercase">
          Regler, byggetilladelse og krav ved nyt tag
        </h3>
        <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
          Når du får <strong>nyt tag</strong>, er der flere regler, der kan få betydning – afhængigt af hvad der ændres på boligen. Vi hjælper dig med at afklare det hele, men her er de vigtigste forhold, du bør kende.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-2">
          <div className="p-6 bg-white border border-stone-200 space-y-3">
            <h4 className="font-bold text-lg text-stone-900">
              Kræver nyt tag byggetilladelse?
            </h4>
            <p className="text-sm text-stone-700 leading-relaxed">
              I de fleste tilfælde kræver en almindelig tagudskiftning <strong>ikke byggetilladelse</strong>, hvis tagets hældning ikke ændres, konstruktionen ikke ændres væsentligt, og tagtypen forbliver den samme.
            </p>
            <p className="text-xs text-stone-600">
              Der kan dog være krav om byggetilladelse, hvis du ændrer tagets hældning, hæver taget, etablerer beboelse i tagetagen eller ændrer konstruktionen væsentligt.
            </p>
          </div>

          <div className="p-6 bg-white border border-stone-200 space-y-3">
            <h4 className="font-bold text-lg text-stone-900">
              Må man ændre tagtype?
            </h4>
            <p className="text-sm text-stone-700 leading-relaxed">
              Det afhænger af lokalplanen, hvor du bor. I mange boligområder er der krav til tagmateriale, farve, overflade og taghældning.
            </p>
            <p className="text-xs text-stone-600">
              Hvis din bolig ligger i et område med lokalplan eller servitutter, kan der være begrænsninger for fx skift fra tegl til stål eller fra rødt til sort tag. Inden vi anbefaler en løsning, undersøger vi altid, hvad der gælder for din adresse.
            </p>
          </div>

          <div className="p-6 bg-white border border-stone-200 space-y-3">
            <h4 className="font-bold text-lg text-stone-900">
              Udløser nyt tag krav om efterisolering?
            </h4>
            <p className="text-sm text-stone-700 leading-relaxed">
              Ved større renoveringer kan Bygningsreglementet stille krav om forbedring af isoleringen – hvis det er teknisk muligt og økonomisk rentabelt.
            </p>
            <p className="text-xs text-stone-600">
              Det betyder, at hvis taget alligevel åbnes, og isoleringen er utilstrækkelig, kan der være krav om at efterisolere. I praksis giver det ofte god mening at forbedre isoleringen samtidig med tagudskiftningen.
            </p>
          </div>

          <div className="p-6 bg-white border border-stone-200 space-y-3">
            <h4 className="font-bold text-lg text-stone-900">
              Asbest i ældre tage
            </h4>
            <p className="text-sm text-stone-700 leading-relaxed">
              Har boligen <strong>eternittag fra 1960–1980’erne</strong>, kan der være asbest i materialet.
            </p>
            <p className="text-xs text-stone-600">
              Asbest kræver korrekt nedtagning og bortskaffelse efter gældende regler. Hos Dansk Tagbyg er vi autoriserede og sørger for, at arbejdet udføres ansvarligt og i overensstemmelse med lovgivningen.{' '}
              <Link to="/asbest-autoriseret" className="text-[#BE0F12] font-bold underline">
                Læs om asbest nedtagning her
              </Link>.
            </p>
          </div>
        </div>
      </div>

      {/* Sådan foregår en tagudskiftning hos os (Trin 1 - 5) */}
      <div className="space-y-6">
        <h3 className="text-2xl sm:text-3xl font-extrabold text-stone-900 uppercase">
          Sådan foregår en tagudskiftning hos os
        </h3>
        <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
          At få lagt nyt tag skal være en tryg proces – ikke en stressfaktor. Med mange gennemførte tagprojekter bag os ved vi, hvor vigtigt det er med <strong>klar kommunikation, realistiske tidsplaner og ordentlig planlægning</strong> fra start.
        </p>

        <div className="space-y-4">
          <div className="p-6 bg-white border border-stone-200 space-y-2">
            <h4 className="font-bold text-base text-stone-900">1. Besigtigelse og ærlig rådgivning</h4>
            <p className="text-sm text-stone-700 leading-relaxed">
              Vi starter med at gennemgå dit tag grundigt. Vi ser på <strong>konstruktion, undertag, isolering og eventuelle skader</strong> – og vi siger det, som det er. Hvis taget kan holde i nogle år endnu, får du det at vide. Hvis det bør skiftes, forklarer vi hvorfor.
            </p>
          </div>
          <div className="p-6 bg-white border border-stone-200 space-y-2">
            <h4 className="font-bold text-base text-stone-900">2. Skriftligt og gennemskueligt tilbud</h4>
            <p className="text-sm text-stone-700 leading-relaxed">
              Du modtager et <strong>tilbud</strong>, hvor det tydeligt fremgår, hvad der er inkluderet. Ingen uklare formuleringer eller skjulte poster. Vi gennemgår det gerne sammen med dig, så du ved præcis, hvad du siger ja til.
            </p>
          </div>
          <div className="p-6 bg-white border border-stone-200 space-y-2">
            <h4 className="font-bold text-base text-stone-900">3. Planlægning og materialevalg</h4>
            <p className="text-sm text-stone-700 leading-relaxed">
              Når du accepterer tilbuddet, <strong>planlægger vi opstart og bestiller materialer</strong>. Vi hjælper dig med at vælge den løsning, der passer til både hus og budget – og vi holder dig opdateret undervejs.
            </p>
          </div>
          <div className="p-6 bg-white border border-stone-200 space-y-2">
            <h4 className="font-bold text-base text-stone-900">4. Udførelse med respekt for din bolig</h4>
            <p className="text-sm text-stone-700 leading-relaxed">
              Vores håndværkere møder til tiden og arbejder effektivt. Vi passer på din ejendom, rydder løbende op og sørger for, at byggepladsen ikke fylder mere end nødvendigt. <strong>Vi overholder de aftalte tidsrammer</strong> – også når vejret driller.
            </p>
          </div>
          <div className="p-6 bg-white border border-stone-200 space-y-2">
            <h4 className="font-bold text-base text-stone-900">5. Gennemgang og aflevering</h4>
            <p className="text-sm text-stone-700 leading-relaxed">
              Når taget er færdigt, <strong>gennemgår vi arbejdet sammen med dig</strong>. Før vi forlader adressen, sikrer vi os, at alt er, som det skal være – både på taget og omkring huset.
            </p>
          </div>
        </div>
        <p className="text-xs text-stone-600">
          De fleste tagprojekter tager <strong>2–4 uger afhængigt af størrelse, kompleksitet og vejrforhold</strong>. Du kan som regel blive boende i huset under hele forløbet.
        </p>
      </div>

      {/* Nyt tag på gammelt hus & Hvor længe holder et nyt tag */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-stone-700 text-sm sm:text-base leading-relaxed">
        <div className="space-y-4">
          <h3 className="text-2xl font-bold text-stone-900 uppercase">
            Nyt tag på gammelt hus
          </h3>
          <p>
            Har du et ældre hus, kan et nyt tag være en god investering, men der er ofte flere ting at tage højde for end ved nyere boliger. Inden arbejdet går i gang, er det vigtigt at undersøge den eksisterende tagkonstruktion, så eventuelle problemer med spær, undertag, fugt eller isolering bliver opdaget.
          </p>
          <p>
            Når du får nyt tag på et gammelt hus, kan det samtidig være en oplagt anledning til at forbedre isoleringen og ventilationen på loftet. Det kan mindske varmetabet og være med til at sikre en sund tagkonstruktion mange år frem.
          </p>
        </div>

        <div className="space-y-4">
          <h3 className="text-2xl font-bold text-stone-900 uppercase">
            Hvor længe holder et nyt tag?
          </h3>
          <p>
            Levetiden på et nyt tag afhænger især af det valgte tagmateriale, kvaliteten af udførelsen og hvor udsat taget er for vind og vejr. Vælger du den rigtige løsning og sørger for løbende vedligeholdelse, kan et nyt tag holde i mange årtier.
          </p>
          <p>
            Tegl- og skifertage er blandt de tagtyper, der typisk har en lang levetid, mens eksempelvis tagpap, eternit, beton og stål har forskellige forventede levetider afhængigt af produkt og konstruktion. Et tags samlede levetid afhænger desuden af undertag, ventilation, inddækninger og korrekt montering.
          </p>
        </div>
      </div>

      {/* Er nyt tag en god investering? */}
      <div className="p-8 bg-stone-50 border border-stone-200 space-y-4 text-stone-700 text-sm sm:text-base leading-relaxed">
        <h3 className="text-2xl font-bold text-stone-900 uppercase">
          Er nyt tag en god investering?
        </h3>
        <p>
          Ja – i mange tilfælde er det en af de mest fornuftige forbedringer, du kan lave på din bolig. Et nyt tag kan:
        </p>
        <ul className="list-disc pl-5 space-y-1 text-stone-800 text-sm">
          <li>Forbedre boligens energimærke og reducere varmetab</li>
          <li>Øge salgsprisen og gøre boligen mere attraktiv ved salg</li>
          <li>Mindske risikoen for dyre fugt- og følgeskader</li>
          <li>Reducere løbende vedligeholdelse</li>
          <li>Give et mærkbart løft i både udtryk og indeklima</li>
        </ul>
        <p>
          Samtidig fjerner du usikkerheden omkring et gammelt eller slidt tag. Mange boligejere oplever, at roen i maven næsten er lige så værdifuld som den økonomiske gevinst.
        </p>
      </div>

      {/* PRISBEREGNER SEKTION */}
      <div id="prisberegner-sektion" className="scroll-mt-36">
        <div className="text-center max-w-3xl mx-auto space-y-3 pb-8">
          <h3 className="text-2xl sm:text-4xl font-extrabold text-stone-900 uppercase">
            Beregn prisen på dit nye tag på 1 minut
          </h3>
          <p className="text-stone-600 text-sm sm:text-base">
            Vælg tagtype, areal, hældning og tilvalg – så får du med det samme et vejledende prisestimat.
          </p>
        </div>
        <PriceCalculator />
      </div>

      {/* Derfor vælger boligejere Dansk Tagbyg */}
      <div className="bg-stone-900 text-white p-8 sm:p-12 space-y-6">
        <h3 className="text-2xl sm:text-3xl font-extrabold uppercase">
          Derfor vælger boligejere Dansk Tagbyg
        </h3>
        <p className="text-stone-300 text-sm sm:text-base leading-relaxed">
          Når du investerer i et nyt tag, skal du kunne stole på dem, der udfører arbejdet. Hos Dansk Tagbyg får du:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 text-sm text-stone-200">
          <div className="p-4 bg-stone-800 border border-stone-700">
            <strong className="block text-white mb-1">⭐ 4,8/5 stjerner</strong>
            Fra over 150 verificerede anmeldelser på Anmeld Håndværker.
          </div>
          <div className="p-4 bg-stone-800 border border-stone-700">
            <strong className="block text-white mb-1">🇩🇰 Landsdækkende service</strong>
            Vi kører ud til opgaver i hele Danmark.
          </div>
          <div className="p-4 bg-stone-800 border border-stone-700">
            <strong className="block text-white mb-1">👤 Fast kontaktperson</strong>
            Gennem hele forløbet taler du direkte med fagfolk.
          </div>
          <div className="p-4 bg-stone-800 border border-stone-700">
            <strong className="block text-white mb-1">🛡️ Dokumenterede garantier</strong>
            Swisspearl og Byg Garanti / DI Byggeri medlemskab.
          </div>
          <div className="p-4 bg-stone-800 border border-stone-700">
            <strong className="block text-white mb-1">🔨 Solid erfaring</strong>
            Hundredevis af gennemførte tagprojekter siden 2016.
          </div>
          <div className="p-4 bg-stone-800 border border-stone-700">
            <strong className="block text-white mb-1">⏱️ Aftaler overholdes</strong>
            Faste tidsplaner og gennemskuelige priser.
          </div>
        </div>

        {/* Lasse Kontaktboks */}
        <div className="pt-6 border-t border-stone-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h4 className="font-bold text-lg text-white">Klar til at få en pris på nyt tag?</h4>
            <p className="text-xs text-stone-400">
              Du er altid velkommen til at tage direkte fat i Lasse, Dansk Tagbyg.
            </p>
          </div>
          <div className="flex flex-wrap gap-4 text-xs font-bold">
            <a
              href={COMPANY_INFO.phoneTel}
              className="px-5 py-2.5 bg-[#BE0F12] hover:bg-red-700 text-white flex items-center gap-2 uppercase tracking-wider"
            >
              <i className="fa-solid fa-phone"></i>
              <span>Ring {COMPANY_INFO.phoneFormatted}</span>
            </a>
            <a
              href={COMPANY_INFO.emailMailto}
              className="px-5 py-2.5 bg-stone-800 hover:bg-stone-700 text-white border border-stone-700 flex items-center gap-2 uppercase tracking-wider"
            >
              <i className="fa-solid fa-envelope"></i>
              <span>{COMPANY_INFO.email}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Find svar på ofte stillede spørgsmål (FAQ) */}
      <div className="space-y-6">
        <h3 className="text-2xl sm:text-3xl font-extrabold text-stone-900 uppercase">
          Find svar på ofte stillede spørgsmål
        </h3>
        <div className="divide-y divide-stone-200 border-y border-stone-200">
          
          {/* FAQ 1: Hvad koster et nyt tag? */}
          <div className="py-4">
            <button
              onClick={() => setOpenFaq(prev => prev.includes(0) ? prev.filter(i => i !== 0) : [...prev, 0])}
              className="w-full flex items-center justify-between text-left font-bold text-base sm:text-lg text-stone-900 hover:text-[#BE0F12] transition-colors py-1 cursor-pointer"
            >
              <span>Hvad koster et nyt tag?</span>
              <i className={`fa-solid fa-chevron-down text-xs transition-transform ${openFaq.includes(0) ? 'rotate-180 text-[#BE0F12]' : ''}`}></i>
            </button>
            {openFaq.includes(0) && (
              <div className="pt-3 pb-2 text-stone-700 text-sm leading-relaxed space-y-3">
                <p>
                  Prisen på et nyt tag afhænger af tagtype, størrelse, konstruktion og adgangsforhold. Når man undersøger prisen på nyt tag, er det især materialevalg og tagets kompleksitet, der spiller ind. De fleste parcelhuse ligger typisk i et samlet spænd fra ca. 120.000 kr. og op, men det kan både være lavere og højere afhængigt af forholdene.
                </p>
                <div className="overflow-x-auto my-2">
                  <table className="min-w-full text-xs border border-stone-200">
                    <thead className="bg-stone-100 font-bold text-stone-900 border-b border-stone-200">
                      <tr>
                        <th className="p-2 text-left">Tagtype</th>
                        <th className="p-2 text-left">Typisk pris pr. m²</th>
                        <th className="p-2 text-left">Forventet levetid</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-200 text-stone-700">
                      <tr><td className="p-2 font-medium">Betontagsten</td><td className="p-2">ca. 900–1.400 kr.</td><td className="p-2">30–40 år</td></tr>
                      <tr><td className="p-2 font-medium">Tegltag</td><td className="p-2">ca. 1.200–1.800 kr.</td><td className="p-2">50+ år</td></tr>
                      <tr><td className="p-2 font-medium">Tagpap</td><td className="p-2">ca. 800–1.400 kr.</td><td className="p-2">30–50 år</td></tr>
                      <tr><td className="p-2 font-medium">Ståltag</td><td className="p-2">ca. 700–1.200 kr.</td><td className="p-2">30–40 år</td></tr>
                      <tr><td className="p-2 font-medium">Skifer</td><td className="p-2">ca. 1.600–2.500 kr.</td><td className="p-2">60+ år</td></tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-stone-500 italic">
                  Priserne er vejledende og afhænger af konstruktion, undertag, isolering, stillads m.m.
                </p>
                <p>
                  Har du prøvet vores <Link to="/prisberegner" className="text-[#BE0F12] font-bold underline">prisberegner for nyt tag</Link>? Her kan du få en mere konkret pris på netop dit tagprojekt.
                </p>
              </div>
            )}
          </div>

          {/* FAQ 2: Hvilken type tag er den bedste til min bolig? */}
          <div className="py-4">
            <button
              onClick={() => setOpenFaq(prev => prev.includes(1) ? prev.filter(i => i !== 1) : [...prev, 1])}
              className="w-full flex items-center justify-between text-left font-bold text-base sm:text-lg text-stone-900 hover:text-[#BE0F12] transition-colors py-1 cursor-pointer"
            >
              <span>Hvilken type tag er den bedste til min bolig?</span>
              <i className={`fa-solid fa-chevron-down text-xs transition-transform ${openFaq.includes(1) ? 'rotate-180 text-[#BE0F12]' : ''}`}></i>
            </button>
            {openFaq.includes(1) && (
              <div className="pt-3 pb-2 text-stone-700 text-sm leading-relaxed space-y-2">
                <p>
                  Der findes mange forskellige tagtyper, som alle har deres forskellige styrker. Valg af type, materiale, farve, glasur mv. handler derfor alt sammen om dine krav og prioriteringer – samt hvad der vil være bedst for dit hus.
                </p>
                <p>
                  Derfor er det vigtigt, at du afgør med dig selv, hvad der er det vigtigste for dig og dit tag, hvor du her bør overveje aspekter som: Pris, kvalitet, holdbarhed, udseende, monteringsproces og miljøbevidsthed. Vi anbefaler derfor altid, at man sætter sig ind i de forskellige tagtyper eller spørger en fagekspert om råd, hvis du har brug for yderligere viden og vejledning.
                </p>
              </div>
            )}
          </div>

          {/* FAQ 3: Hvornår er det nødvendigt at skifte taget på min bolig? */}
          <div className="py-4">
            <button
              onClick={() => setOpenFaq(prev => prev.includes(2) ? prev.filter(i => i !== 2) : [...prev, 2])}
              className="w-full flex items-center justify-between text-left font-bold text-base sm:text-lg text-stone-900 hover:text-[#BE0F12] transition-colors py-1 cursor-pointer"
            >
              <span>Hvornår er det nødvendigt at skifte taget på min bolig?</span>
              <i className={`fa-solid fa-chevron-down text-xs transition-transform ${openFaq.includes(2) ? 'rotate-180 text-[#BE0F12]' : ''}`}></i>
            </button>
            {openFaq.includes(2) && (
              <div className="pt-3 pb-2 text-stone-700 text-sm leading-relaxed space-y-2">
                <p>
                  Dit tag er boligens vigtigste værn mod vind og vejr, og det skal derfor være i god stand for at beskytte huset mod fugt og kulde.
                </p>
                <p>
                  Tegn på, at det kan være tid til nyt tag:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm">
                  <li>Utætheder, vandindtrængning eller fugtskjolder på loftet</li>
                  <li>Revnede, porøse eller smuldrende tagsten/plader</li>
                  <li>Undertaget er mørt, hængende eller utæt</li>
                  <li>Taget har nået sin forventede levetid (typisk 30-50+ år afhængig af type)</li>
                  <li>Uforholdsmæssigt store udgifter til løbende reparationer</li>
                </ul>
              </div>
            )}
          </div>

          {/* FAQ 4: Hvilke fordele er der ved at få nyt tag? */}
          <div className="py-4">
            <button
              onClick={() => setOpenFaq(prev => prev.includes(3) ? prev.filter(i => i !== 3) : [...prev, 3])}
              className="w-full flex items-center justify-between text-left font-bold text-base sm:text-lg text-stone-900 hover:text-[#BE0F12] transition-colors py-1 cursor-pointer"
            >
              <span>Hvilke fordele er der ved at få nyt tag?</span>
              <i className={`fa-solid fa-chevron-down text-xs transition-transform ${openFaq.includes(3) ? 'rotate-180 text-[#BE0F12]' : ''}`}></i>
            </button>
            {openFaq.includes(3) && (
              <div className="pt-3 pb-2 text-stone-700 text-sm leading-relaxed space-y-2">
                <p>
                  Et nyt tag er en af de bedste investeringer, du kan foretage i din bolig. Fordelene inkluderer:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm">
                  <li><strong>Bedre indeklima og mindre træk</strong> i alle husets rum</li>
                  <li><strong>Lavere varmeregning</strong> – især hvis der efterisoleres samtidig</li>
                  <li><strong>Markant visuelt løft</strong> af hele husets facade og udtryk</li>
                  <li><strong>Højere gensalgsværdi</strong> og bedre energimærkning ved et eventuelt salg</li>
                  <li><strong>Fuldkommen ro i maven</strong> i årtier med stærke produkt- og udførelsesgarantier</li>
                </ul>
              </div>
            )}
          </div>

          {/* FAQ 5: Hvor lang tid tager det at lave et nyt tag? */}
          <div className="py-4">
            <button
              onClick={() => setOpenFaq(prev => prev.includes(4) ? prev.filter(i => i !== 4) : [...prev, 4])}
              className="w-full flex items-center justify-between text-left font-bold text-base sm:text-lg text-stone-900 hover:text-[#BE0F12] transition-colors py-1 cursor-pointer"
            >
              <span>Hvor lang tid tager det at lave et nyt tag?</span>
              <i className={`fa-solid fa-chevron-down text-xs transition-transform ${openFaq.includes(4) ? 'rotate-180 text-[#BE0F12]' : ''}`}></i>
            </button>
            {openFaq.includes(4) && (
              <div className="pt-3 pb-2 text-stone-700 text-sm leading-relaxed space-y-2">
                <p>
                  De fleste tagprojekter tager <strong>2–4 uger</strong> fra opstart til færdigt afleveret tag afhængigt af boligens størrelse, tagets kompleksitet, vejret og materialevalg.
                </p>
                <p>
                  Du kan som regel <strong>blive boende i huset under hele forløbet</strong>. Vores håndværkere møder til tiden, rydder løbende op og afdækker forsvarligt, så boligen altid er beskyttet mod vind og vejr.
                </p>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Vi motiveres af glade kunder og gode resultater */}
      <div className="bg-stone-50 border border-stone-200 p-8 sm:p-12 space-y-8">
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <h3 className="text-2xl sm:text-4xl font-extrabold text-stone-900 uppercase">
            Vi motiveres af glade kunder og gode resultater
          </h3>
          <div className="flex items-center justify-center gap-2 text-amber-500 text-sm">
            <div className="flex">
              <i className="fa-solid fa-star"></i>
              <i className="fa-solid fa-star"></i>
              <i className="fa-solid fa-star"></i>
              <i className="fa-solid fa-star"></i>
              <i className="fa-solid fa-star"></i>
            </div>
            <span className="font-bold text-stone-800">4,8 ud af 155 anmeldelser</span>
            <span className="text-stone-400">•</span>
            <span className="text-stone-600 font-medium">Finalist til Årets Håndværker 5 år i træk</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {REVIEWS.slice(0, 6).map((rev) => (
            <div key={rev.id} className="bg-white p-6 border border-stone-200 shadow-sm flex flex-col justify-between space-y-4">
              <p className="text-xs sm:text-sm text-stone-700 italic leading-relaxed">
                "{rev.quote}"
              </p>
              <div className="pt-3 border-t border-stone-100 flex items-center justify-between">
                <span className="font-bold text-xs text-stone-900">{rev.author}, {rev.location}</span>
                <div className="flex text-amber-500 text-xs">
                  <i className="fa-solid fa-star"></i>
                  <i className="fa-solid fa-star"></i>
                  <i className="fa-solid fa-star"></i>
                  <i className="fa-solid fa-star"></i>
                  <i className="fa-solid fa-star"></i>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bund: Anmeldelser og Kontaktformular (som på originalen) */}
      <section id="kontakt" className="py-16 sm:py-24 bg-stone-100 scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 sm:gap-16 items-start">
            
            {/* Venstre kolonne: Firmaoplysninger og Anmeld Håndværker */}
            <div className="lg:col-span-5 space-y-8">
              <div className="p-6 bg-white border border-stone-200 shadow-sm space-y-3">
                <div className="flex items-center gap-1 text-amber-500 text-lg">
                  <i className="fa-solid fa-star"></i>
                  <i className="fa-solid fa-star"></i>
                  <i className="fa-solid fa-star"></i>
                  <i className="fa-solid fa-star"></i>
                  <i className="fa-solid fa-star"></i>
                </div>
                <a
                  href="https://www.anmeld-haandvaerker.dk/tagdaekker/dansk-tagbyg-aps"
                  target="_blank"
                  rel="noreferrer"
                  className="block font-black text-xl text-stone-900 hover:text-[#BE0F12] transition-colors"
                >
                  4,8 ud af 155 anmeldelser
                </a>
                <p className="text-xs text-stone-500">
                  Finalist til Årets Håndværker 5 år i træk
                </p>
              </div>

              <div className="p-6 bg-white border border-stone-200 shadow-sm space-y-4 text-sm text-stone-700">
                <h3 className="font-extrabold text-xl text-stone-900 uppercase">
                  Dansk Tagbyg ApS
                </h3>
                <div className="space-y-2">
                  <p>
                    <strong className="text-stone-900">Ring:</strong>{' '}
                    <a href={`tel:${COMPANY_INFO.phone}`} className="text-[#BE0F12] font-bold hover:underline">
                      {COMPANY_INFO.phoneFormatted}
                    </a>
                  </p>
                  <p>
                    <strong className="text-stone-900">Skriv:</strong>{' '}
                    <a href={`mailto:${COMPANY_INFO.email}`} className="text-[#BE0F12] font-bold hover:underline">
                      {COMPANY_INFO.email}
                    </a>
                  </p>
                  <p>
                    <strong className="text-stone-900">Adr:</strong> {COMPANY_INFO.address}
                  </p>
                  <p>
                    <strong className="text-stone-900">CVR:</strong> {COMPANY_INFO.cvr}
                  </p>
                </div>
                <div className="pt-3 border-t border-stone-200">
                  <p className="font-bold text-stone-900">Vi løser opgaver i hele DK</p>
                </div>
                <div className="pt-2">
                  <p className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Vi er medlem af</p>
                  <div className="flex flex-wrap gap-2 text-xs font-semibold text-stone-700">
                    <span className="px-2.5 py-1 bg-stone-100 border border-stone-200">Swisspearl</span>
                    <span className="px-2.5 py-1 bg-stone-100 border border-stone-200">Byg Garanti</span>
                    <span className="px-2.5 py-1 bg-stone-100 border border-stone-200">DI Byggeri</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Højre kolonne: Kontaktformular */}
            <div className="lg:col-span-7 bg-white border border-stone-200 p-8 sm:p-10 shadow-sm">
              <ContactForm idPrefix="new-roof" submitButtonText="Send" />
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
